import { apiClient } from "@lib/axios";

export const collaborationApi = {
  auth: async (payload?: { room?: string }) => {
    return await apiClient.post<{ token: string }>(
      "/collaboration/authorize",
      payload,
    );
  },
  // Read the current history map + contentPointer straight from Mongo. The
  // history dialog uses this on open: every collab autosave already records its
  // revision server-side, so Mongo is the complete, up-to-date source — no need
  // to force a Yjs flush just to list revisions.
  getHistory: async (params: { storyId: string; versionId?: string | null }) => {
    const search = new URLSearchParams({ storyId: params.storyId });
    if (params.versionId) search.set("versionId", params.versionId);
    return await apiClient.get<{
      history: Record<
        string,
        { userIds: string[]; url: string; revisions: number }
      > | null;
      contentPointer: string | null;
    }>(`/collaboration/history?${search.toString()}`);
  },
  // Record one collaborative revision after the editing client has uploaded its
  // converged snapshot to GCS. The backend atomically bumps the shared session's
  // `history` entry and returns the fresh history map for the history dialog.
  recordRevision: async (payload: {
    storyId: string;
    versionId?: string | null;
    url: string;
    sessionId: string;
    userId?: string;
    previewUrl?: string;
    // The pointer this client's content descends from. The backend refuses to
    // promote this revision to head if it no longer matches the stored head.
    parentPointer?: string | null;
  }) => {
    return await apiClient.post<{
      history: Record<
        string,
        { userIds: string[]; url: string; revisions: number }
      > | null;
      // The pointer the backend kept after its guards. May differ from the url
      // we just uploaded (an older/out-of-order or stale-parent revision is
      // frozen out).
      contentPointer: string | null;
      // False when the revision was recorded but NOT made the head. Older
      // backends omit it; treat a missing value as promoted.
      promoted?: boolean;
    }>("/collaboration/record-revision", payload);
  },
  // Short-lived JWT for the self-hosted Hocuspocus Yjs server, scoped to one
  // room. Mirrors `auth` but targets the Hocuspocus token endpoint.
  hocuspocusToken: async (payload: { room: string }) => {
    return await apiClient.post<{ token: string }>(
      "/collaboration/hocuspocus-token",
      payload,
    );
  },
};
